/* Version: 3.5.137 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Item } from 'react-stately';
import PropTypes from 'prop-types';
import Errors from '../Errors/Errors.js';
import Label from '../Label/Label.js';
import Select from '../Select/Select.js';
import { Root } from '../Select/styledComponents.js';
import Stack from '../Stack/Stack.js';
import { safeRest, hasErrors, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { DROPDOWN_VARIANT } from './constants.js';
import DropdownSelect from './DropdownSelect.js';
import { makeMenuItems, getSelectedIndex, getSelectedItemDetails, getDisabledKeys } from './utils.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useTheme from '../../hooks/useTheme.js';
import componentWithName from '../../prop-types/componentWithName.js';

var _excluded = ["ariaDescribedBy", "ariaLabel", "ariaLabelledBy", "ariaRequired", "callouts", "customStyle", "disabled", "dropdownItems", "errors", "id", "label", "onChange", "outline", "placeholder", "selected", "tooltip", "value", "variant"];
var Dropdown = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _customStyle$rootStyl, _customStyle$rootStyl2, _customStyle$rootStyl3;
  var ariaDescribedBy = _ref.ariaDescribedBy,
    ariaLabel = _ref.ariaLabel,
    ariaLabelledBy = _ref.ariaLabelledBy,
    ariaRequired = _ref.ariaRequired,
    _ref$callouts = _ref.callouts,
    callouts = _ref$callouts === undefined ? false : _ref$callouts,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      rootStyle: {},
      labelStyle: {},
      dropdownInputStyle: {},
      dropdownMenuStyle: {}
    } : _ref$customStyle,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === undefined ? false : _ref$disabled,
    _ref$dropdownItems = _ref.dropdownItems,
    dropdownItems = _ref$dropdownItems === undefined ? [] : _ref$dropdownItems,
    _ref$errors = _ref.errors,
    errors = _ref$errors === undefined ? [] : _ref$errors,
    id = _ref.id,
    label = _ref.label,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === undefined ? noop : _ref$onChange,
    outline = _ref.outline,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === undefined ? '' : _ref$placeholder,
    _ref$selected = _ref.selected,
    selected = _ref$selected === undefined ? 0 : _ref$selected,
    tooltip = _ref.tooltip,
    _ref$value = _ref.value,
    value = _ref$value === undefined ? null : _ref$value,
    _ref$variant = _ref.variant,
    variant = _ref$variant === undefined ? DROPDOWN_VARIANT.BASIC : _ref$variant,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useTheme = useTheme(),
    dropdown = _useTheme.dropdown;
  var native = dropdown.native;
  var safeProps = safeRest(rest);
  var _safeCustomStyle = safeCustomStyle(customStyle),
    rootStyle = _safeCustomStyle.rootStyle;
  var items = makeMenuItems(dropdownItems, placeholder, native);
  var _useState = useState(getSelectedIndex(items, value, selected)),
    _useState2 = _slicedToArray(_useState, 2),
    selectedIndex = _useState2[0],
    setSelectedIndex = _useState2[1];
  var hasError = hasErrors(errors);
  var errorsId = hasErrors(errors) ? "".concat(id, "-errors") : ariaDescribedBy;
  useEffect(function () {
    setSelectedIndex(getSelectedIndex(items, value, selected));
  }, [items, selected, value]); // Only re-run the effect if selected prop changes

  var dropDownFocus = useRef(null);

  // memoized change handler
  var handleChange = useCallback(function (event, ind) {
    if (native && getSelectedIndex(items, event.target.value) >= 0) {
      setSelectedIndex(getSelectedIndex(items, event.target.value));
      handle(onChange)(event, event.target.value);
    } else if (items.length > ind && !items[ind].disabled) {
      setSelectedIndex(ind);
      handle(onChange)(event, items[ind].value); // callback with actual value of selected item
    } else ;
  }, [native, items, onChange]);
  var selectedItem = getSelectedItemDetails(items, selectedIndex);

  // Native
  if (native) {
    return /*#__PURE__*/jsxs(Root, {
      rootStyle: rootStyle,
      children: [label && /*#__PURE__*/jsx(Label, {
        customStyle: {
          labelStyle: customStyle.labelStyle
        },
        htmlFor: "".concat(id, "-toggle"),
        id: "".concat(id, "-label"),
        tooltip: tooltip,
        children: label
      }), /*#__PURE__*/jsxs(Stack, {
        align: "flex-start",
        gap: "0",
        wrap: "nowrap",
        style: {
          position: 'relative'
        },
        children: [/*#__PURE__*/jsx(DropdownSelect, _objectSpread({
          ariaLabel: ariaLabel,
          ariaLabelledBy: ariaLabelledBy,
          ariaRequired: ariaRequired,
          ariaDescribedBy: errorsId,
          customStyle: {
            dropdownInputStyle: customStyle.dropdownInputStyle
          },
          disabled: disabled,
          dropdownItems: items,
          id: id,
          label: label,
          outline: outline || hasError,
          onChange: handleChange,
          placeholder: placeholder,
          ref: ref,
          selected: selectedIndex,
          variant: variant
        }, safeProps)), /*#__PURE__*/jsx(Errors, {
          id: id,
          callouts: callouts,
          errors: errors
        })]
      })]
    });
  }

  // Non-native
  return /*#__PURE__*/jsx(Select, {
    "aria-describedby": errorsId,
    "aria-invalid": outline || hasError,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy || "".concat(id, "-label"),
    "aria-required": ariaRequired,
    callouts: callouts,
    customStyle: {
      rootStyle: customStyle.rootStyle,
      labelStyle: customStyle.labelStyle,
      inputStyle: customStyle.dropdownInputStyle,
      menuStyle: customStyle.dropdownMenuStyle
    },
    disabled: disabled,
    disabledKeys: getDisabledKeys(dropdownItems),
    errors: errors,
    id: id,
    label: label,
    onSelectionChange: function onSelectionChange(val) {
      setSelectedIndex(getSelectedIndex(items, val));
      handle(onChange)(null, val);
    },
    outline: outline || hasError,
    placeholder: placeholder,
    ref: dropDownFocus,
    selectedKey: selectedItem === null || selectedItem === undefined ? undefined : selectedItem.value,
    style: {
      '--mux-input-max-width': customStyle === null || customStyle === undefined || (_customStyle$rootStyl = customStyle.rootStyle) === null || _customStyle$rootStyl === undefined ? undefined : _customStyle$rootStyl.maxWidth,
      '--mux-input-min-width': customStyle === null || customStyle === undefined || (_customStyle$rootStyl2 = customStyle.rootStyle) === null || _customStyle$rootStyl2 === undefined ? undefined : _customStyle$rootStyl2.minWidth,
      '--mux-input-width': customStyle === null || customStyle === undefined || (_customStyle$rootStyl3 = customStyle.rootStyle) === null || _customStyle$rootStyl3 === undefined ? undefined : _customStyle$rootStyl3.width
    },
    tooltip: tooltip,
    variant: variant,
    children: dropdownItems.map(function (item) {
      return /*#__PURE__*/jsx(Item, {
        textValue: item.value,
        children: item.label
      }, item.value);
    })
  });
});
process.env.NODE_ENV !== "production" ? Dropdown.propTypes = {
  /**
   * ARIA attribute [`aria-describedby`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-describedby).
   */
  ariaDescribedBy: PropTypes.string,
  /**
   * ARIA attribute [`aria-label`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label).
   */
  ariaLabel: PropTypes.string,
  /**
   * ARIA attribute [`aria-labelledby`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-labelledby).
   */
  ariaLabelledBy: PropTypes.string,
  /**
   * ARIA attribute [`aria-required`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-required).
   */
  ariaRequired: PropTypes.bool,
  /**
   * Sets `aria-atomic=true`, `aria-live=polite` and `role=alert` on error messages (ensures errors are announced by screen readers as soon as they happen).
   */
  callouts: PropTypes.bool,
  /** Style overrides */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object,
    // root wrapper
    labelStyle: PropTypes.object,
    // label
    dropdownInputStyle: PropTypes.object,
    // input
    dropdownMenuStyle: PropTypes.object // popover menu (non-native only)
  }),
  /**
   * The list of Dropdown items (controlled).
   */
  dropdownItems: PropTypes.arrayOf(PropTypes.shape({
    /** value for the item, equivalent to option value in select element */
    value: PropTypes.string,
    /** label for the item, equivalent to option label in select element */
    label: PropTypes.string.isRequired,
    /** Item is not selectable if it is disabled */
    disabled: PropTypes.bool
  })),
  /**
   * Whether the input is disabled. Maps to the HTML [`disabled`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled) attribute.
   */
  disabled: PropTypes.bool,
  /**
   * An array of error messages to be displayed. Passing an array also enables error styling, and sets aria-invalid="true".
   */
  errors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    msg: PropTypes.any,
    isError: PropTypes.bool
  }))]),
  /**
   * Global [`id`](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id) attribute.
   */
  id: PropTypes.string,
  /**
   * Label for the input.
   */
  label: PropTypes.node,
  /**
   * Handler that is called when the selected value changes.
   */
  onChange: PropTypes.func,
  /**
   * Flag to set error styling without displaying errors; useful if handling errors yourself using the [Errors](/docs/errors) component.
   */
  outline: PropTypes.bool,
  /**
   * Temporary text that occupies the text input when it is empty.
   */
  placeholder: PropTypes.string,
  /**
   * Index of selected item.
   *
   * **NOTE:** This prop will be getting deprecated in a future release. Use `value` instead.
   */
  selected: PropTypes.number,
  /** [Tooltip](/docs/tooltip) displayed beside the label */
  tooltip: componentWithName('Tooltip'),
  /**
   * Specifies which style of input to use.
   */
  variant: PropTypes.oneOf([DROPDOWN_VARIANT.BASIC, DROPDOWN_VARIANT.BASIC_LINE, DROPDOWN_VARIANT.BASIC_LINE_DASHED]),
  /**
   * Controlled value of the Dropdown.
   */
  value: PropTypes.string
} : undefined;
process.env.NODE_ENV !== "production" ? Dropdown.displayName = 'Dropdown' : undefined;

export { Dropdown as default };
